import { Flex, InputCheckbox, Label, Span } from "@goodfill/ui-components";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilState } from "recoil";
import { FieldGroup } from "../../../../../../form/FieldGroup";
import { CustomerApplicationFormState } from "../../../formState/formState";
import { FormYesOrNoQuestion } from "../../FormYesOrNoQuestion";
import { SignatureBox } from "../../SignatureBox";
import { validStep1State } from "../Step1SignUp/step1SignUpState";

const VITE_INTERACTIVE_BROKERS_TERMS_LINK = import.meta.env.VITE_INTERACTIVE_BROKERS_TERMS_LINK;

const promptMessage = `
By typing my signature and sending it via the internet, I acknowledge that I 
have read and understand all information provided during the account application 
process; that I intend Interactive Brokers to rely upon it; that I intend to be 
bound to the terms of the Customer Agreement (which contains a pre-dispute 
arbitration clause in the paragraph noted at the end of the Agreement) and the other 
agreements and disclosures provided to me above; and that I understand and agree 
that my electronic signature is the equivalent of the manual written signature.
`;

const permissionsOptionsBehalf: Array<{
  label: string;
  key:
    | "allowBondTrading"
    | "allowChangingAccountInformationAfterUserInstruction"
    | "allowTransmittingReceivedOrders"
    | "allowConfirmAccountInformation";
}> = [
  { label: "Allow bond trading", key: "allowBondTrading" },
  {
    label: "Changing your account information as instructed by you",
    key: "allowChangingAccountInformationAfterUserInstruction",
  },
  { label: "Transmitting orders received from you", key: "allowTransmittingReceivedOrders" },
  { label: "Confirming your account information", key: "allowConfirmAccountInformation" },
];

const permissionsOptionsRespondingTo: Array<{
  label: string;
  key:
    | "allowRespondingToTenders"
    | "allowRespondingToPutOptionsContingentUponCorporateAction"
    | "allowRespondingToPutOptionsNotContingentUponCorporateAction"
    | "allowOtherElectionsByBondHolders";
}> = [
  { label: "Tenders", key: "allowRespondingToTenders" },
  {
    label: "Put Options - Contingent upon corporate action",
    key: "allowRespondingToPutOptionsContingentUponCorporateAction",
  },
  {
    label: "Put Options - Not Contingent upon corporate action",
    key: "allowRespondingToPutOptionsNotContingentUponCorporateAction",
  },
  { label: "All other elections by bondholders", key: "allowOtherElectionsByBondHolders" },
];

export const Step8TradingPermissions = ({ areAllDisabled }: { areAllDisabled: boolean }) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<CustomerApplicationFormState>();

  const [validStep1] = useRecoilState(validStep1State);

  return (
    <Flex direction="col" horizontalPadding={8} verticalPadding={8} borderBottom="thin" items="center">
      <Span size="2xl" uppercase={false} font="semibold">
        Trading Permissions
      </Span>

      <Span className="Steps_welcomeMessage" wrap={true}>
        To open a free account and start you investing safely, we need to know more about you. Let's get started!
      </Span>

      <Flex wrap={true} className="w-full max-w-2xl mt-8 my-4">
        <Span font="medium" size="lg" className="px-0 font-bold" wrap={true}>
          Allow Goodfill USA, LLC to act on your behalf in:
        </Span>

        {permissionsOptionsBehalf.map((permission) => (
          <Controller
            key={permission.key}
            render={({ field, fieldState: { error } }) => (
              <FormYesOrNoQuestion
                id={permission.key}
                key={permission.key}
                label={permission.label}
                value={field.value}
                validation={error?.message ? [error.message] : []}
                onChange={field.onChange}
                fill
                onBlur={field.onBlur}
                ref={field.ref}
                disabled={areAllDisabled}
                className="w-full-important"
              />
            )}
            // as cast hack needed, see: https://react-hook-form.com/api/usefieldarray
            name={`step8.${permission.key}` as "step8.allowRespondingToTenders"}
            control={control}
          />
        ))}
      </Flex>

      <Flex wrap={true} className="w-full max-w-2xl mt-8 my-4">
        <Span font="medium" size="lg" className="px-0 font-bold" wrap={true}>
          Allow Goodfill USA, LLC to act on your behalf in responding to:
        </Span>

        {permissionsOptionsRespondingTo.map((permission) => (
          <Controller
            key={permission.key}
            render={({ field, fieldState: { error } }) => (
              <FormYesOrNoQuestion
                id={permission.key}
                key={permission.key}
                label={permission.label}
                value={field.value}
                validation={error?.message ? [error.message] : []}
                onChange={field.onChange}
                fill
                onBlur={field.onBlur}
                ref={field.ref}
                disabled={areAllDisabled}
                className="w-full-important"
              />
            )}
            // as cast hack needed, see: https://react-hook-form.com/api/usefieldarray
            name={`step8.${permission.key}` as "step8.allowBondTrading"}
            control={control}
          />
        ))}
      </Flex>

      <Flex wrap={true} className="w-full max-w-2xl mt-8 my-4">
        <Controller
          render={({ field }) => (
            <FieldGroup label="" validation={[errors.step8?.isInteractiveBrokersTermsConfirmed?.message ?? ""]} fill>
              <Span font="medium" size="lg" className="px-0" wrap={true}>
                Agreement
              </Span>
              <Flex direction="row">
                <div className="mt-1 pr-2 pt-2">
                  <InputCheckbox
                    id={"isInteractiveBrokersTermsConfirmed"}
                    checked={field.value === true}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                </div>

                <Label htmlFor={"isInteractiveBrokersTermsConfirmed"} primary={field.value === true}>
                  <a target={"_blank"} rel="noreferrer" href={VITE_INTERACTIVE_BROKERS_TERMS_LINK}>
                    I have read & agreed to the following Interactive Brokers documents
                  </a>
                  .
                </Label>
              </Flex>
            </FieldGroup>
          )}
          name="step8.isInteractiveBrokersTermsConfirmed"
          control={control}
        />

        <Controller
          render={({ field }) => (
            <FieldGroup label="Signature" validation={[errors.step8?.signature?.message ?? ""]} fill={true}>
              <SignatureBox
                promptMessage={promptMessage}
                value={field.value}
                firstName={validStep1?.firstName ?? ""}
                lastName={validStep1?.lastName ?? ""}
                setValue={field.onChange}
                isValid={errors.step8?.signature === undefined}
                onBlur={field.onBlur}
                ref={field.ref}
                id="signature"
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name="step8.signature"
          control={control}
        />
      </Flex>
    </Flex>
  );
};
